import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiFeil } from '../../../api/klient';
import { ENDELSER, filApi } from '../../../api/filer';
import type { Sporsmal } from '../../../api/reise';

/** Filen som er lastet opp til spørsmålet. Navnet er tomt når svaret kommer fra serveren. */
export interface ValgtFil {
  filId: string;
  filnavn: string;
}

/** Filen i et svar som er gitt (`{ filId }`). */
export function filFraSvar(verdi: unknown): ValgtFil | null {
  const filId = (verdi as { filId?: unknown } | null)?.filId;
  return typeof filId === 'string' ? { filId, filnavn: '' } : null;
}

interface Props {
  id: string;
  sporsmal: Sporsmal;
  fil: ValgtFil | null;
  onEndre: (fil: ValgtFil | null) => void;
}

/**
 * Et spørsmål av typen `fil` (T070): filen lastes opp med en gang den er valgt, og svaret er `{ filId }`, som
 * lagres når kunden går videre. Filtypene følger spørsmålets `filtype`.
 */
export default function Filfelt({ id, sporsmal, fil, onEndre }: Props) {
  const { t } = useTranslation();
  const [laster, setLaster] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);

  async function velg(valgt: File | undefined) {
    if (!valgt) return;
    setFeil(null);
    setLaster(true);
    try {
      const lagret = await filApi.lastOpp(valgt, { sporsmalId: sporsmal.id, instans: sporsmal.instans });
      onEndre({ filId: lagret.id, filnavn: lagret.filnavn });
    } catch (e) {
      setFeil(e instanceof ApiFeil ? e.kode : 'ukjent');
    } finally {
      setLaster(false);
    }
  }

  return (
    <div>
      <label htmlFor={`${id}-fil`} className="mb-2 block font-medium">
        {t('reise.fil.velg')}
      </label>
      <input
        id={`${id}-fil`}
        type="file"
        accept={ENDELSER[sporsmal.filtype ?? 'annet']}
        disabled={laster}
        onChange={(e) => velg(e.target.files?.[0])}
        className="block w-full text-sm"
      />
      <p role="status" className="mt-2 text-sm text-slate-700">
        {laster ? t('reise.fil.laster') : fil ? (fil.filnavn ? t('reise.fil.lastetOpp', { fil: fil.filnavn }) : t('reise.oversikt.fil')) : ''}
      </p>
      {feil && (
        <p role="alert" className="mt-2 melding-feil">
          {t(`feil.${feil}`, { defaultValue: t('feil.ukjent') })}
        </p>
      )}
    </div>
  );
}
